import React, { useContext, useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { UserContext } from "../context/UserContext";
import axios from "axios";
import "./Home.css";
import RandomQuote from "../components/RandomQuote";
import BookRecommendation from "./BookRecommendation";

import Avatar1 from "../components/avatars/avatar1";
import Avatar2 from "../components/avatars/avatar2";
import Avatar3 from "../components/avatars/avatar3";
import Avatar4 from "../components/avatars/avatar4";
import Avatar5 from "../components/avatars/avatar5";
import Avatar6 from "../components/avatars/avatar6";
import Avatar7 from "../components/avatars/avatar7";
import Avatar8 from "../components/avatars/avatar8";
import Avatar9 from "../components/avatars/avatar9";
import AvatarDefault from "../components/avatars/AvatarDefault";

const avatarMap = {
  default: AvatarDefault,
  avatar1: Avatar1,
  avatar2: Avatar2,
  avatar3: Avatar3,
  avatar4: Avatar4,
  avatar5: Avatar5,
  avatar6: Avatar6,
  avatar7: Avatar7,
  avatar8: Avatar8,
  avatar9: Avatar9,
};

const Home = () => {
  const { user } = useContext(UserContext);
  const navigate = useNavigate();

  const [topWishlist, setTopWishlist] = useState([]);
  const [topCompleted, setTopCompleted] = useState([]);
  const [fullProfile, setFullProfile] = useState(null);

  const handleNavigate = (path) => {
    if (path === "/match-list" && !user) {
      navigate("/login");
    } else {
      navigate(path);
    }
  };

  useEffect(() => {
    const fetchTopLists = async () => {
      try {
        const [wishlistRes, completedRes] = await Promise.all([
          axios.get(
            "https://bookmatchup.onrender.com/api/lists/top-wishlist?limit=5"
          ),
          axios.get(
            "https://bookmatchup.onrender.com/api/lists/top-completed?limit=5"
          ),
        ]);
        setTopWishlist(wishlistRes.data);
        setTopCompleted(completedRes.data);
      } catch (error) {
        console.error("Error fetching top lists:", error);
      }
    };

    fetchTopLists();
  }, []);

  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!user) return;
      try {
        const res = await axios.get(
          `https://bookmatchup.onrender.com/api/user/${user.id || user._id}`
        );
        setFullProfile(res.data);
      } catch (err) {
        console.error("Error fetching profile for home avatar:", err);
      }
    };

    fetchUserProfile();
  }, [user]);

  return (
    <div className="home-container">
      <div className="home-header fade-in">
        {user ? (
          <div className="personal-greeting">
            <h1>
              Welcome back,{" "}
              <span style={{ color: fullProfile?.avatarColor || "#b42a2a" }}>
                {user.username}
              </span>
            </h1>
            {fullProfile?.avatar && (
              <div
                className="greeting-avatar-wrapper"
                style={{ borderColor: fullProfile.avatarColor || "#b42a2a" }}
              >
                {React.createElement(
                  avatarMap[fullProfile.avatar] || AvatarDefault,
                  {
                    color: fullProfile.avatarColor || "#b42a2a",
                    size: 50,
                  }
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="guest-welcome">
            <h1>Welcome to Book Matchup</h1>
            <p className="login-note">
              Don’t have an account?{" "}
              <Link to="/register" className="link">
                Register here
              </Link>
              .
            </p>
            <p className="login-note">
              Already registered?{" "}
              <Link to="/login" className="link">
                Login here
              </Link>
              .
            </p>
          </div>
        )}

        <p>Discover new reads and connect with like-minded readers.</p>
        <div className="home-image-wrapper">
          <img
            src="/images/open-book-27.jpg"
            alt="Open Book"
            className="home-banner-image"
          />
        </div>
        <RandomQuote />
        <BookRecommendation />
      </div>

      {/* Search + Matches */}
      <div className="home-sections">
        <div className="home-card">
          <h2>Search for Books</h2>
          <p>Find books to wishlist, complete, and review.</p>
          <button onClick={() => handleNavigate("/search")}>Search</button>
        </div>

        <div className="home-card">
          <h2>Find Reader Matches</h2>
          <p>Connect based on shared books and reviews.</p>
          <button onClick={() => handleNavigate("/match-list")}>
            Find Matches
          </button>
        </div>
      </div>

      <hr className="home-divider" />

      {/* 📚 Top Lists */}
      <div className="top-lists-container">
        <div className="top-list-section">
          <h2>Top Wishlisted Books</h2>
          <ul className="top-list">
            {topWishlist.length > 0 ? (
              topWishlist.map((item) => (
                <li key={item._id}>
                  <Link
                    to={`/book/${encodeURIComponent(item.bookDetails.itemId)}`}
                  >
                    {item.bookDetails.cover_url && (
                      <img
                        src={item.bookDetails.cover_url}
                        alt={item.bookDetails.title}
                        className="book-cover-thumb"
                      />
                    )}
                    {item.bookDetails.title}
                  </Link>
                </li>
              ))
            ) : (
              <li>Loading...</li>
            )}
          </ul>
          <Link to="/top-wishlist" className="view-all-link">
            View All Wishlisted ➔
          </Link>
        </div>

        <div className="top-list-section">
          <h2>Top Completed Books</h2>
          <ul className="top-list">
            {topCompleted.length > 0 ? (
              topCompleted.map((item) => (
                <li key={item._id}>
                  <Link
                    to={`/book/${encodeURIComponent(item.bookDetails.itemId)}`}
                  >
                    {item.bookDetails.cover_url && (
                      <img
                        src={item.bookDetails.cover_url}
                        alt={item.bookDetails.title}
                        className="book-cover-thumb"
                      />
                    )}
                    {item.bookDetails.title}
                  </Link>
                </li>
              ))
            ) : (
              <li>Loading...</li>
            )}
          </ul>
          <Link to="/top-completed" className="view-all-link">
            View All Completed ➔
          </Link>
        </div>
      </div>

      <hr className="home-divider" />

      {/* ✅ Your Lists */}
      <div className="home-sections">
        <div className="home-card">
          <h2>Your Wishlist</h2>
          <p>Manage books you're excited to read next.</p>
          <button onClick={() => handleNavigate("/wishlist")}>
            View Wishlist
          </button>
        </div>

        <div className="home-card">
          <h2>Your Completed List</h2>
          <p>Track books you've finished and loved.</p>
          <button onClick={() => handleNavigate("/completed")}>
            View Completed
          </button>
        </div>
      </div>
    </div>
  );
};

export default Home;
